import { Link } from 'react-router-dom';
import { BUSINESS_INFO } from '../../mocks/lisaMerkleData';

interface FinalCTAProps {
  title?: string;
  subtitle?: string;
  ctaText?: string;
  ctaLink?: string;
  bgImage?: string;
}

export default function FinalCTA({
  title = 'Ready to Transform Your Space?',
  subtitle = 'Schedule your private design consultation and discover what\'s possible for your home.',
  ctaText = 'Schedule a Design Consultation',
  ctaLink = '/location#contact',
  bgImage = 'https://public.readdy.ai/ai/img_res/1958a7f151af8a88da6a1e51ae8ae63c.jpg',
}: FinalCTAProps) {
  return (
    <section className="relative overflow-hidden">
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-[8000ms] ease-out hover:scale-105"
        style={{ backgroundImage: `url(${bgImage})` }}
      />
      {/* Strong cinematic overlay for readability */}
      <div className="absolute inset-0 bg-charcoal/80" />
      <div className="absolute inset-0 bg-gradient-to-b from-charcoal/30 via-charcoal/60 to-charcoal/85" />

      {/* Left edge decorative line */}
      <div className="absolute left-8 inset-y-0 items-center hidden xl:flex">
        <div className="w-px h-32 bg-stone/30" />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-8 py-32 md:py-40">
        <div className="max-w-3xl mx-auto text-center">

          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="w-10 h-px bg-stone/60" />
            <span className="text-stone text-[10px] tracking-[0.28em] uppercase font-sans">Beverly Hills Interior Design</span>
            <div className="w-10 h-px bg-stone/60" />
          </div>

          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-light text-white leading-[1.05] mb-6 text-balance">
            {title}
          </h2>

          <div className="w-10 h-px bg-stone mx-auto my-8" />

          <p className="text-cream/90 text-base md:text-lg font-sans font-light max-w-xl mx-auto mb-12 leading-relaxed">
            {subtitle}
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link
              to={ctaLink}
              className="px-10 py-4 bg-cream text-charcoal text-[10px] tracking-[0.2em] uppercase font-medium font-sans rounded-full hover:bg-white hover:scale-[1.02] transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              {ctaText}
            </Link>
            <a
              href={BUSINESS_INFO.phoneHref}
              className="flex items-center gap-2.5 px-10 py-4 border border-cream/50 text-cream text-[10px] tracking-[0.2em] uppercase font-medium font-sans rounded-full hover:bg-cream/15 hover:border-cream/70 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line text-stone text-sm" />
              {BUSINESS_INFO.phone}
            </a>
          </div>

          {/* Trust strip */}
          <div className="flex items-center justify-center gap-6 mt-14">
            <div className="flex items-center gap-2">
              <i className="ri-award-line text-stone text-sm" />
              <span className="text-cream/80 text-[10px] font-sans tracking-wider">UCLA Trained</span>
            </div>
            <div className="w-px h-3 bg-cream/30" />
            <span className="text-cream/80 font-display italic text-xs">Elle Décor</span>
            <div className="w-px h-3 bg-cream/30" />
            <span className="text-cream/80 font-display italic text-xs">Architectural Digest</span>
          </div>
        </div>
      </div>
    </section>
  );
}